import type { HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import type {
  AuditRecord,
  AuditRecordsPage,
  AuditSortOrder,
  GetAuditRecordParams,
  ListAuditRecordsParams,
} from "./audit.types";

export const AUDIT_PATHS = {
  LIST: "/api/plataforma/auditoria",
} as const;

/** Recurso do detalhe de um registro; conforme o item 834, apenas GET. */
export function buildAuditRecordPath(id: string): string {
  return `${AUDIT_PATHS.LIST}/${encodeURIComponent(id)}`;
}

/** Ordenação padrão da consulta: do mais recente para o mais antigo. */
export const DEFAULT_AUDIT_ORDER: AuditSortOrder = "desc";

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readText(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function readFirstText(raw: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const text = readText(raw[key]).trim();
    if (text) return text;
  }

  return "";
}

function readCount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
}

function readFirstCount(raw: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const count = readCount(raw[key]);
    if (count !== null) return count;
  }

  return null;
}

function extractItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  for (const key of ["data", "items", "content", "registros", "auditoria"]) {
    const nested = payload[key];
    if (nested !== undefined) return extractItems(nested);
  }

  return [];
}

/**
 * Registro único do detalhamento. Só desce pelo envelope quando o campo é um
 * objeto ou uma lista: o próprio registro pode trazer a data em `data`.
 */
function extractDetail(payload: unknown): unknown {
  if (Array.isArray(payload)) return payload[0];
  if (!isRecord(payload)) return payload;

  for (const key of ["data", "item", "content", "registro", "auditoria"]) {
    const nested = payload[key];
    if (isRecord(nested) || Array.isArray(nested)) return extractDetail(nested);
  }

  return payload;
}

/**
 * O filtro de Ano vira o período do primeiro ao último dia do ano escolhido:
 * a consulta recorta por data, e não por um campo de ano. As duas pontas vão em
 * ISO 8601 (UTC), do primeiro instante do dia 1º ao último instante de 31/12,
 * para que o ano inteiro caiba no intervalo.
 */
export function buildAuditYearRange(ano: number): { dataInicio: string; dataFim: string } {
  return {
    dataInicio: new Date(Date.UTC(ano, 0, 1, 0, 0, 0, 0)).toISOString(),
    dataFim: new Date(Date.UTC(ano, 11, 31, 23, 59, 59, 999)).toISOString(),
  };
}

function toAuditRecord(raw: unknown, index: number): AuditRecord | null {
  if (!isRecord(raw)) return null;

  const dataHora = readFirstText(raw, "dataHora", "data_hora", "createdAt", "created_at", "data");
  const acao = readFirstText(raw, "acao", "action", "alteracao");
  const modulo = readFirstText(raw, "modulo", "module");
  const responsavel = readFirstText(raw, "responsavel", "responsible", "usuario", "createdBy");
  if (!dataHora && !acao && !modulo && !responsavel) return null;

  return {
    // O índice mantém a linha endereçável (chave da tabela, ação Detalhar) sem id.
    id: readFirstText(raw, "id", "auditoriaId") || `${dataHora}-${index}`,
    dataHora,
    acao,
    valorAnterior: readFirstText(raw, "valorAnterior", "valor_anterior", "previousValue"),
    valorNovo: readFirstText(raw, "valorNovo", "valor_novo", "newValue"),
    justificativa: readFirstText(raw, "justificativa", "justification"),
    modulo,
    responsavel,
    responsavelDetalhe: readFirstText(
      raw,
      "responsavelPerfil",
      "responsavel_perfil",
      "perfil",
      "cargo",
      "area",
      "setor",
    ),
    responsavelEmail: readFirstText(
      raw,
      "responsavelEmail",
      "responsavel_email",
      "email",
      "usuarioEmail",
    ),
    responsavelTelefone: readFirstText(
      raw,
      "responsavelTelefone",
      "responsavel_telefone",
      "telefone",
      "phone",
      "celular",
    ),
  };
}

function extractTotal(payload: unknown): number | null {
  if (!isRecord(payload)) return null;

  const meta = isRecord(payload.meta) ? payload.meta : undefined;
  const pagination = isRecord(meta?.pagination)
    ? meta.pagination
    : isRecord(payload.pagination)
      ? payload.pagination
      : undefined;

  for (const source of [pagination, meta, payload]) {
    if (!source) continue;

    const count = readFirstCount(source, "total", "totalRecords", "totalElements", "totalItens");
    if (count !== null) return count;
  }

  return isRecord(payload.data) ? extractTotal(payload.data) : null;
}

function toTimestamp(value: string): number | null {
  const timestamp = Date.parse(value);

  return Number.isNaN(timestamp) ? null : timestamp;
}

/** Reforça a ordem pedida sobre a página devolvida; registros sem data ficam no fim. */
function sortByDataHora(records: AuditRecord[], ordem: AuditSortOrder): AuditRecord[] {
  const direction = ordem === "asc" ? 1 : -1;

  return [...records].sort((a, b) => {
    const first = toTimestamp(a.dataHora);
    const second = toTimestamp(b.dataHora);
    if (first === null && second === null) return 0;
    if (first === null) return 1;
    if (second === null) return -1;

    return (first - second) * direction;
  });
}

/**
 * Consulta somente leitura dos registros de auditoria das Obras.
 *
 * Contrato acordado com o back-end (tarefa 1046): filtros combinados em conjunto
 * (`status`, o período `dataInicio`/`dataFim` do ano escolhido, os códigos de
 * `acao` (a Alteração da tela) e `modulo` e o `responsavelId`), paginação em
 * `pagina`/`tamanhoPagina` e ordenação por data/hora em `ordenarPor`/`ordem`.
 * O recorte às Obras (RN001) é aplicado pelo próprio serviço.
 */
export async function listAuditRecords({
  status,
  ano,
  alteracao,
  modulo,
  responsavelId,
  pagina,
  tamanhoPagina,
  ordem = DEFAULT_AUDIT_ORDER,
  signal,
}: ListAuditRecordsParams = {}): Promise<AuditRecordsPage> {
  const params: Record<string, string | number> = {};
  if (status) params.status = status;
  if (ano) Object.assign(params, buildAuditYearRange(ano));
  // O filtro chama-se Alteração na tela, mas o serviço lê o código como `acao`.
  if (alteracao) params.acao = alteracao;
  if (modulo) params.modulo = modulo;
  if (responsavelId?.trim()) params.responsavel = responsavelId.trim();
  if (pagina) params.pagina = pagina;
  if (tamanhoPagina) params.tamanhoPagina = tamanhoPagina;
  params.ordenarPor = "dataHora";
  params.ordem = ordem;

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(AUDIT_PATHS.LIST, config);

  const records = sortByDataHora(
    extractItems(response.data)
      .map(toAuditRecord)
      .filter((record): record is AuditRecord => record !== null),
    ordem,
  );

  return { records, total: extractTotal(response.data) ?? records.length };
}

/**
 * Detalhe somente leitura do registro acionado em Detalhar.
 *
 * Contrato do item 834: `GET /api/plataforma/auditoria/{id}`, sem nenhuma
 * escrita (RN004/RN005). Devolve `null` quando a resposta chega sem o registro
 * — a tela trata esse caso como registro não encontrado.
 */
export async function getAuditRecord({
  id,
  signal,
}: GetAuditRecordParams): Promise<AuditRecord | null> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildAuditRecordPath(id), config);
  const record = toAuditRecord(extractDetail(response.data), 0);

  // RN001 — o detalhe pertence à linha acionada: vale o identificador consultado.
  return record ? { ...record, id } : null;
}
